import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './sic-footer.css.lit';

import { SlotsNGController as SlotController } from '../../controllers/slots-ng-controller';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { ResizeController } from '../../controllers/resize-controller';
import { screenSizeMap } from '../../controllers/screen-size-controller';

/**
 * The footer component
 * @element sic-footer
 *
 * @slot menu
 * @slot highlighted-links
 * @slot social-links-wrapper
 * @slot footer-bottom
 *
 * @prop breakpoint
 *
 * Lightdom styles in sic-footer.lightdom.css included in /shared.css
 */

@customElement('sic-footer')
export class SicFooter extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(this);
  resizeController: ResizeController;

  // The resizeController is initialized here because visibleBreakpoint is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [screenSizeMap.get(this.visibleBreakpoint) as number],
    });
  }

  @property({ type: String, attribute: 'breakpoint' })
  visibleBreakpoint = 'md';
  // This breakpoint needs to match @screeen breakpoints in sic-footer-light-dom.css

  desktopTemplate(): TemplateResult {
    return html`
      <section class="footer desktop">
        <div class="footer-top">
          <div class="footer-navigation">
            ${this.slots.conditionalSlot('menu', false)}
          </div>
          <div class="footer-sidebar">
            ${this.slots.conditionalSlot('highlighted-links', false)}
            ${this.slots.conditionalSlot('social-links-wrapper', false)}
          </div>
        </div>
        <hr class="footer-hr" />
        ${this.slots.conditionalSlot('footer-bottom', false)}
      </section>
    `;
  }

  mobileTemplate(): TemplateResult {
    return html`
      <section class="footer mobile">
        <div class="footer-top">
          ${this.slots.conditionalSlot('highlighted-links', false)}
          ${this.slots.conditionalSlot('menu', false)}
          ${this.slots.conditionalSlot('social-links-wrapper', false)}
        </div>
        <hr class="footer-hr" />
        ${this.slots.conditionalSlot('footer-bottom', false)}
      </section>
    `;
  }

  render(): TemplateResult {
    const isMobile = this.resizeController.currentBreakpointRange === 0;

    return html`
      <sic-container
        background-color="primary-lightest"
        container-width="wide"
        x-padding="normal"
        top-padding="normal"
        bottom-padding="normal"
        class="${isMobile ? 'mobile' : ''}"
      >
        ${isMobile ? this.mobileTemplate() : this.desktopTemplate()}
      </sic-container>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-footer': SicFooter;
  }
}
